import { useRouter } from 'next/router';
import * as React from 'react';
import Section from '../Section';
import { centeredMainGridClasses } from '@/styles/grid';
import { PaginationSection } from 'components/src/pagination';
import Label from 'components/src/label';
import Link from 'next/link';
import { formatSearchLink } from '@/utils/utils';
import SearchBox from './SearchBox';
import { SearchItemsTypesQuery, SearchItemsQueryVariables } from './Types';
import { useQuery } from '@apollo/client';
import { SearchItems } from '@/clients/queries/search/SearchItems.graphql';
import { Maybe, SearchSuggestionFieldsFragment } from 'types/generated';
import SearchInfoText from './SearchInfoText';
import SearchSuggestions from './SearchSuggestions';

const PAGE_SIZE = 6;

type Props = {
  suggestions: Array<Maybe<SearchSuggestionFieldsFragment>>;
};

const SearchResult = ({ title, uri, type }: { title: string; uri: string; type: string }) => {
  const isProduct = type === 'product';
  return (
    <div className="mt-40">
      <div className="flex items-center gap-8">
        {isProduct && <Label label="Vefverslun" type="pill" colorVariant="disabled" />}
        <Link
          href={formatSearchLink(uri, type)}
          className="block w-fit text-md text-red-100 hover:underline hover:decoration-red-100"
        >
          {title}
        </Link>
      </div>
    </div>
  );
};

const SearchPage = ({ suggestions }: Props) => {
  const router = useRouter();
  const [searchValue, setSearchValue] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const minSearchStringLength = 3;
  const startSearch = searchValue ? searchValue.length >= minSearchStringLength : false;

  React.useEffect(() => {
    const query = router.query;

    if (query && query.query !== undefined) {
      const queryValue = Array.isArray(query.query) ? query.query[0] : query.query;
      setSearchValue(decodeURI(queryValue));
    }
  }, [router]);

  React.useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const { data, loading } = useQuery<SearchItemsTypesQuery, SearchItemsQueryVariables>(SearchItems, {
    variables: {
      searchString: searchValue || '',
      from: (page - 1) * PAGE_SIZE,
      size: PAGE_SIZE,
    },
    skip: !startSearch,
  });

  const noDataFound = !loading && (!data?.searchItems || Number(data.searchItems.length) === 0);
  const totalPages = Math.ceil((data?.searchItems?.[0]?.hits ?? 0) / PAGE_SIZE);

  return (
    <>
      <Section
        childrenTypeName={undefined}
        sectionId={'Search'}
        theme={'white-15'}
        marginTop={64}
        marginBottom={32}
        gridType={'full'}
      >
        <div className={centeredMainGridClasses}>
          <div className="col-span-full md:col-span-9 md:col-start-2">
            <SearchBox searchValue={searchValue} setSearchValue={setSearchValue} />
          </div>
        </div>
      </Section>
      <Section
        childrenTypeName={undefined}
        sectionId={'ProductCollection'}
        theme={'white-15'}
        marginTop={32}
        marginBottom={128}
        gridType={'full'}
      >
        <div className={centeredMainGridClasses}>
          <div className="col-span-full min-h-[500px] md:col-span-9 md:col-start-2">
            <SearchInfoText
              searchValue={searchValue}
              loading={loading}
              noDataFound={startSearch && noDataFound}
              startSearch={startSearch}
            />
            {startSearch ? (
              data?.searchItems.map((item) => (
                <SearchResult key={item.id} title={item.heading} uri={item.uri} type={item.type} />
              ))
            ) : (
              <SearchSuggestions suggestions={suggestions} onItemClicked={(item) => setSearchValue(item)} />
            )}
            {totalPages > 1 && (data?.searchItems?.length ?? 0) > 0 && (
              <>
                <div className="my-32 w-full border-t-1 border-black-25" />
                <PaginationSection
                  pages={totalPages}
                  currentPage={page}
                  alignment="center"
                  onClick={(page) => {
                    setPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              </>
            )}
          </div>
        </div>
      </Section>
    </>
  );
};
export default SearchPage;
